// Intake Form Completion Percentage Calculator
// Based on different user types and form sections

import { RIGHTS_FIELDS } from '../components/RightsIntakeForm';
import { careerQuestions } from '../components/CareerIntakeLogic';
import { emotionalQuestions } from '../components/EmotionalIntakeLogic';

export type UserType = 'social_worker' | 'career_counselor' | 'emotional_therapist' | 'general';

export interface IntakeSection {
  name: string;
  nameHe: string;
  fields: string[];
  required: boolean;
  userTypes: UserType[];
}

export interface CompletionResult {
  overall: number;
  sections: {
    [sectionName: string]: {
      completed: number;
      total: number;
      percentage: number;
      required: boolean;
    };
  };
  requiredSectionsComplete: boolean;
  totalFields: number;
  completedFields: number;
}

// Define intake sections based on the spreadsheet structure
export const INTAKE_SECTIONS: IntakeSection[] = [
  {
    name: 'personal_info',
    nameHe: 'מידע אישי',
    fields: ['firstName', 'lastName', 'dateOfBirth', 'governmentId', 'gender', 'maritalStatus', 'education'],
    required: true,
    userTypes: ['social_worker', 'career_counselor', 'emotional_therapist', 'general']
  },
  {
    name: 'contact_info', 
    nameHe: 'פרטי התקשרות',
    fields: ['email', 'phone', 'address'],
    required: true,
    userTypes: ['social_worker', 'career_counselor', 'emotional_therapist', 'general']
  },
  {
    name: 'social_work',
    nameHe: 'עבודה סוציאלית',
    fields: RIGHTS_FIELDS.map(f => f.field_name),
    required: false,
    userTypes: ['social_worker']
  },
  {
    name: 'career_guidance',
    nameHe: 'הכוונה מקצועית',
    fields: careerQuestions.map(q => q.field_name),
    required: false,
    userTypes: ['career_counselor']
  },
  {
    name: 'emotional_therapy',
    nameHe: 'טיפול רגשי',
    fields: emotionalQuestions.map(q => q.field_name),
    required: false,
    userTypes: ['emotional_therapist']
  },
  {
    name: 'general_intake',
    nameHe: 'זיוף אינטייק/סיכום מקיף על התהליך',
    fields: ['strengths', 'obstacles', 'notes'],
    required: false,
    userTypes: ['social_worker', 'career_counselor', 'emotional_therapist', 'general']
  }
];

/**
 * Calculate intake form completion percentage for a specific user type
 */
export function calculateIntakeCompletion(
  patientData: any,
  userType: UserType
): CompletionResult {
  const relevantSections = INTAKE_SECTIONS.filter(section => 
    section.userTypes.includes(userType)
  );

  const sectionResults: CompletionResult['sections'] = {};
  let totalFields = 0;
  let completedFields = 0;
  let requiredSectionsComplete = true;

  for (const section of relevantSections) {
    const sectionTotal = section.fields.length;
    const sectionCompleted = section.fields.filter(field => {
      const value = getNestedValue(patientData, field);
      return isFieldCompleted(value);
    }).length;

    const sectionPercentage = sectionTotal > 0 ? Math.round((sectionCompleted / sectionTotal) * 100) : 0;

    sectionResults[section.name] = {
      completed: sectionCompleted,
      total: sectionTotal,
      percentage: sectionPercentage,
      required: section.required
    };

    totalFields += sectionTotal;
    completedFields += sectionCompleted;

    // Check if required sections are complete
    if (section.required && sectionPercentage < 100) {
      requiredSectionsComplete = false;
    }
  }

  const overallPercentage = totalFields > 0 ? Math.round((completedFields / totalFields) * 100) : 0;

  return {
    overall: overallPercentage,
    sections: sectionResults,
    requiredSectionsComplete,
    totalFields,
    completedFields
  };
}

/**
 * Get completion percentage for all user types
 */
export function getAllUserTypeCompletions(patientData: any): {
  [userType in UserType]: CompletionResult;
} {
  return {
    social_worker: calculateIntakeCompletion(patientData, 'social_worker'),
    career_counselor: calculateIntakeCompletion(patientData, 'career_counselor'),
    emotional_therapist: calculateIntakeCompletion(patientData, 'emotional_therapist'),
    general: calculateIntakeCompletion(patientData, 'general')
  };
}

/**
 * Get user type based on user role
 */
export function getUserTypeFromRole(role: string): UserType {
  switch (role) {
    case 'social_worker':
      return 'social_worker';
    case 'career_counselor':
      return 'career_counselor';
    case 'emotional_therapist':
      return 'emotional_therapist';
    default:
      return 'general';
  }
}

/**
 * Check if a field value is considered "completed"
 */
function isFieldCompleted(value: any): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (typeof value === 'number') return !isNaN(value);
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Object.keys(value).length > 0;
  return Boolean(value);
}

/**
 * Get nested value from object using dot notation
 */
function getNestedValue(obj: any, path: string): any {
  return path.split('.').reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : undefined;
  }, obj);
}

/**
 * Get completion status color
 */
export function getCompletionColor(percentage: number): string {
  if (percentage >= 90) return '#28a745'; // Green
  if (percentage >= 70) return '#ffc107'; // Yellow
  if (percentage >= 50) return '#fd7e14'; // Orange
  return '#dc3545'; // Red
}

/**
 * Get completion status text
 */
export function getCompletionStatus(percentage: number, language: 'en' | 'he' = 'en'): string {
  if (language === 'he') {
    if (percentage >= 90) return 'הושלם';
    if (percentage >= 70) return 'כמעט הושלם';
    if (percentage >= 50) return 'בתהליך';
    return 'התחיל';
  } else {
    if (percentage >= 90) return 'Complete';
    if (percentage >= 70) return 'Nearly Complete';
    if (percentage >= 50) return 'In Progress';
    return 'Started';
  }
}

/**
 * Calculate average intake completion percentage for all active cases assigned to a user
 */
export function calculateUserIntakeCompletion(
  userId: string,
  patients: any[],
  getPatientData: (caseId: string) => Promise<any>
): Promise<number> {
  return new Promise(async (resolve) => {
    try {
      // Get all active cases assigned to this user
      const activeCases = patients.filter(p => 
        p.status === 'active' && 
        Array.isArray(p.assignedAdmins) && 
        p.assignedAdmins.includes(userId)
      );

      if (activeCases.length === 0) {
        resolve(0);
        return;
      }

      // Calculate completion for each case
      const completionPromises = activeCases.map(async (patient) => {
        try {
          const patientData = await getPatientData(patient.caseId);
          const completion = calculateIntakeCompletion(patientData, 'general');
          return completion.overall;
        } catch (error) {
          console.error(`Error calculating completion for case ${patient.caseId}:`, error);
          return 0;
        }
      });

      const completions = await Promise.all(completionPromises);
      
      // Calculate average
      const totalCompletion = completions.reduce((sum, comp) => sum + comp, 0);
      const averageCompletion = Math.round(totalCompletion / completions.length);
      
      resolve(averageCompletion);
    } catch (error) {
      console.error('Error calculating user intake completion:', error);
      resolve(0);
    }
  });
}

/**
 * Calculate average completion percentage of all available forms for a case
 * Includes all forms, even if they haven't been filled yet (0% completion)
 */
export async function calculateCaseFormCompletion(caseId: string): Promise<number> {
  try {
    // Import form management functions
    const { getAllForms, getCaseSubmissions } = await import('../firebase/formManagement');
    
    // Get all active forms
    const forms = await getAllForms();
    
    if (forms.length === 0) {
      return 0;
    }
    
    // Get all submissions for this case
    const submissions = await getCaseSubmissions(caseId);
    
    // Calculate completion for each form (including forms with no submission = 0%)
    const formCompletions: number[] = [];
    
    for (const form of forms) {
      if (!form.fields || form.fields.length === 0) {
        // Skip forms with no fields
        continue;
      }
      
      const submission = submissions.find(s => s.formId === form.id);
      const totalFields = form.fields.length;
      let filledFields = 0;
      
      if (submission && submission.answers) {
        // Calculate completion percentage for this form
        form.fields.forEach(field => {
          const fieldName = field.field_name || field.id;
          const value = submission.answers[fieldName];
          
          // Check if field is filled
          if (value !== undefined && value !== null && value !== '') {
            if (Array.isArray(value)) {
              if (value.length > 0) {
                filledFields++;
              }
            } else {
              filledFields++;
            }
          }
        });
      }
      // If no submission, filledFields remains 0
      
      const completionPercentage = totalFields > 0 ? Math.round((filledFields / totalFields) * 100) : 0;
      
      // Include all forms, even if completion is 0%
      formCompletions.push(completionPercentage);
    }
    
    // Calculate average of all forms
    if (formCompletions.length === 0) {
      return 0;
    }
    
    const sum = formCompletions.reduce((acc, val) => acc + val, 0);
    const average = Math.round(sum / formCompletions.length);
    
    return average;
  } catch (error) {
    console.error('Error calculating case form completion:', error);
    return 0;
  }
}

/**
 * Calculate milestone completion percentage for a case
 * Returns percentage of milestones that are achieved (0-100)
 */
export async function calculateCaseMilestoneCompletion(caseId: string): Promise<number> {
  try {
    const { collection, query, where, getDocs } = await import('firebase/firestore');
    const { db } = await import('../firebase');
    
    const milestonesRef = collection(db, 'milestones');
    const milestonesQuery = query(milestonesRef, where('caseId', '==', caseId));
    const snapshot = await getDocs(milestonesQuery);
    
    if (snapshot.empty) {
      return 0; // No milestones = 0% completion
    }
    
    const milestones = snapshot.docs.map(doc => doc.data());
    const totalMilestones = milestones.length;
    const achievedMilestones = milestones.filter(m => m.status === 'achieved').length;
    
    if (totalMilestones === 0) {
      return 0;
    }
    
    return Math.round((achievedMilestones / totalMilestones) * 100);
  } catch (error) {
    console.error(`Error calculating milestone completion for case ${caseId}:`, error);
    return 0;
  }
}

/**
 * Count the number of meetings (events) for a specific case from events collection
 * Returns object with completed and total counts
 */
export async function countCaseMeetings(caseId: string): Promise<{ completed: number; total: number }> {
  try {
    // Query events collection directly for this case
    const { collection, query, where, getDocs } = await import('firebase/firestore');
    const { db } = await import('../firebase');
    
    const eventsRef = collection(db, 'events');
    
    // Query events for this caseId
    const eventsQuery = query(
      eventsRef,
      where('caseId', '==', caseId)
    );
    
    const snapshot = await getDocs(eventsQuery);
    const caseEvents = snapshot.docs
      .map(doc => ({
        id: doc.id,
        ...doc.data()
      } as any))
      .filter((event: any) => !event.archived); // Filter out archived events
    
    // Count completed events (status === 'completed')
    const completedEvents = caseEvents.filter((event: any) => {
      const status = event.status?.toLowerCase();
      return status === 'completed';
    });
    
    return {
      completed: completedEvents.length,
      total: caseEvents.length
    };
  } catch (error) {
    console.error(`Error counting meetings for case ${caseId}:`, error);
    return { completed: 0, total: 0 };
  }
}

/**
 * Calculate meeting completion percentage for a case
 * Returns percentage of required meetings completed (0-100)
 */
export async function calculateMeetingCompletion(
  caseId: string,
  requiredMeetings: number
): Promise<number> {
  if (!requiredMeetings || requiredMeetings <= 0) {
    return 0; // If no requirement set, return 0
  }
  
  const meetingCounts = await countCaseMeetings(caseId);
  // Use completed meetings vs required meetings
  const percentage = Math.min(Math.round((meetingCounts.completed / requiredMeetings) * 100), 100);
  return percentage;
}

/**
 * Calculate overall process completion (intake + meetings)
 * Returns average of intake completion and meeting completion
 */
export async function calculateOverallProcessCompletion(
  caseId: string,
  intakeCompletion: number,
  requiredMeetings: number
): Promise<number> {
  const meetingCompletion = await calculateMeetingCompletion(caseId, requiredMeetings);
  
  // If no meeting requirement, return only intake completion
  if (!requiredMeetings || requiredMeetings <= 0) {
    return intakeCompletion;
  }
  
  // Average of intake and meeting completion
  return Math.round((intakeCompletion + meetingCompletion) / 2);
}
